import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Building2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth, useLang } from "@/context/AuthContext";
import { BrandLogo, LangSwitch } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t } from "@/lib/i18n";

function DriverForm({ lang, onDone }) {
  const [phone, setPhone] = useState("+40");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/driver/login", { phone, pin });
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err, t(lang, "error")));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="driver-login-form">
      <div className="space-y-1.5">
        <Label>{t(lang, "phone")}</Label>
        <Input data-testid="driver-phone-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 text-lg" required />
        <p className="text-xs text-slate-500">{t(lang, "phone_hint")}</p>
      </div>
      <div className="space-y-1.5">
        <Label>{t(lang, "pin")}</Label>
        <Input data-testid="driver-pin-input" type="password" inputMode="numeric" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="h-12 font-mono text-2xl tracking-[0.5em]" required />
      </div>
      <SubmitBtn busy={busy} label={t(lang, "login")} testId="driver-login-submit" />
    </form>
  );
}

function AdminForm({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
      <div className="space-y-1.5">
        <Label>Email</Label>
        <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" required />
      </div>
      <div className="space-y-1.5">
        <Label>Parolă</Label>
        <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" required />
      </div>
      <SubmitBtn busy={busy} label="Intră în birou" testId="admin-login-submit" />
    </form>
  );
}

const SubmitBtn = ({ busy, label, testId }) => (
  <button type="submit" disabled={busy} data-testid={testId} className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 font-display text-xl font-bold uppercase tracking-wide text-white transition-colors hover:bg-blue-700 active:scale-[0.99] disabled:opacity-60">
    {busy && <Loader2 className="h-5 w-5 animate-spin" />} {label}
  </button>
);

export default function Login() {
  const [mode, setMode] = useState("driver");
  const [lang, setLang] = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const onDone = ({ token, user }) => {
    login(token, user);
    const home = user.role === "admin" ? "/admin" : "/driver";
    const from = loc.state?.from;
    nav(from && from.startsWith(home) ? from : home, { replace: true });
  };

  const tab = (key, Icon, label) => (
    <button type="button" onClick={() => setMode(key)} data-testid={`login-tab-${key}`}
      className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors ${mode === key ? "bg-[#1E293B] text-white" : "text-slate-400 hover:text-white"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col px-5 py-8">
      <div className="flex justify-end"><LangSwitch lang={lang} setLang={setLang} /></div>
      <div className="fade-up mt-8"><BrandLogo className="h-24 sm:h-28" /></div>
      <h1 className="fade-up mt-10 font-display text-4xl font-extrabold uppercase leading-none sm:text-5xl">
        {mode === "driver" ? t(lang, "login_driver_title") : "Panou birou"}
      </h1>
      <p className="mt-3 text-sm text-slate-400">{mode === "driver" ? t(lang, "login_driver_sub") : "Comenzi, șoferi și documente CMR."}</p>
      <div className="mt-8 flex gap-1 rounded-xl border border-white/10 bg-black/30 p-1">
        {tab("driver", Truck, t(lang, "driver"))}
        {tab("admin", Building2, t(lang, "office"))}
      </div>
      <div className="panel mt-4">{mode === "driver" ? <DriverForm lang={lang} onDone={onDone} /> : <AdminForm onDone={onDone} />}</div>
      <p className="mt-auto pt-10 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600">Innovate · Connect · Grow</p>
    </div>
  );
}
